"use client";
import _ from "lodash";
import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { withQuery } from "with-query";

import InfoIcon from "./assets/info-icon.svg?react";
import { factoryUseModalX } from "@/lib/factoryUseModalX";
import fetchSingleton from "@/lib/fetchSingleton";
import {
  INTERRUPTED,
  submitFunction,
  useFormValidation,
} from "@/lib/formValidationHelpers";
import { getZoneName } from "@/lib/getZoneName";
import { humanizeError } from "@/lib/humanizeError";
import { useAuth } from "@/lib/useAuth";
import { useCommunityId } from "@/lib/useCommunityId";
import { useData, useDataList } from "@/lib/useData";
import { useProgramId } from "@/lib/useProgramId";
import { useValidateAddress } from "@/lib/useValidateAddress";
import { useModalQuestion } from "@/ModalQuestion";
import PartAddressSuggestions from "@/PartAddressSuggestions";
import PartFormDatePicker from "@/PartFormDatePicker";
import PartFormError from "@/PartFormError";
import PartFormSaveButton from "@/PartFormSaveButton";
import PartLoader from "@/PartLoader";
import PartModal, { type PartModalRef } from "@/PartModal";
import formStyles from "@/styles/form.module.scss";
import { Address, ApiResponseDto, ErrorT, Zone } from "@/types/api-types";
import { addressEditorSchemaJoi } from "@/validations";

declare global {
  interface Window {
    google: typeof google;
  }
}

type IFormInput = Address;

interface IFormInputRef {
  reset: () => void;
}

export const useModalAddressEditor = factoryUseModalX({
  queryName: "modalAddressEditor",
});

export const AddressEditor = React.forwardRef<
  IFormInputRef,
  { setConfirmClose?: (arg0: boolean) => void }
>(({ setConfirmClose }, ref) => {
  const { isCreating, isEditing, editingId, finishInteraction, startEdit } =
    useModalAddressEditor();

  const { idToken } = useAuth();

  const { communityId } = useCommunityId();

  const formMethods = useForm<IFormInput>({
    reValidateMode: "onSubmit",
  });

  const {
    register,
    formState: { errors, isDirty },
    reset,
    watch,
    control,
    trigger,
    setError,
    clearErrors,
  } = formMethods;

  React.useImperativeHandle(ref, () => ({
    reset: () => reset(),
  }));

  const { handleSubmit, isSubmitting, submitRunner } = useFormValidation<IFormInput>({
    control,
    joiSchema: addressEditorSchemaJoi,
    context: { isEditing, editingId },
    setError,
    clearErrors,
  });

  const loaders = [];

  const [existingItem, existingItemLoading] = useData<Address>(
    process.env.NEXT_PUBLIC_API_URL + `/api/v1/address/${editingId}`,
    { enabled: isEditing },
  );
  loaders.push(existingItemLoading);

  const isAlternateAddress = existingItem?.is_primary === false;
  const isPrimaryOrStandaloneAddress =
    existingItem?.is_primary === true || existingItem?.is_primary === null;

  const maybeParentAddressId = isAlternateAddress
    ? existingItem?.primary_address_id
    : undefined;
  const [maybeParentOfExistingItem, maybeParentOfExistingItemLoading] = useData<Address>(
    process.env.NEXT_PUBLIC_API_URL + `/api/v1/address/${maybeParentAddressId}`,
    { enabled: !!maybeParentAddressId },
  );
  loaders.push(maybeParentOfExistingItemLoading);

  const { programId: selectedProgramIdInUi } = useProgramId();

  const [programsItems, programsItemsLoading] = useDataList<{ id: number; name: string }>(
    process.env.NEXT_PUBLIC_API_URL + "/api/v1/program",
  );
  loaders.push(programsItemsLoading);

  const [zones, isZonesLoading] = useDataList<Zone>(
    withQuery(process.env.NEXT_PUBLIC_API_URL + "/api/v1/zones", {
      community_id: communityId,
    }),
  );
  loaders.push(isZonesLoading);

  React.useEffect(() => {
    if (isZonesLoading) return;

    if (isEditing && existingItem) {
      const existingItem1 = existingItem;

      const existingItem2 = {
        ...existingItem1,
        zone_id: (() => {
          if (existingItem1?.zone_id) return existingItem1?.zone_id;
          if (existingItem1?.zone) return existingItem1.zone.id;
        })(),
      };
      reset(existingItem2);
    }
    if (isCreating) {
      if (zones?.length === 0) {
        toast.warning("No zones in community, create it first", { autoClose: 5000 });
        return;
      }
      reset({
        curbside_households: 1,
        zone_id: zones?.length === 1 ? zones[0].id : undefined,
        program_id: selectedProgramIdInUi ? +selectedProgramIdInUi : undefined,
      });
    }
  }, [
    isEditing,
    isCreating,
    existingItem,
    zones,
    isZonesLoading,
    programsItems,
    programsItemsLoading,
  ]);

  const { startValidate, isValidateRunning } = useValidateAddress({
    submitRunner,
    isEditing,
    editingId,
  });

  const openModalQuestion = useModalQuestion();

  const onSubmit: submitFunction<IFormInput> = async (data, handleApiResult) => {
    let url = process.env.NEXT_PUBLIC_API_URL as string;
    if (isCreating) url += "/api/v1/address";
    else if (isEditing && isPrimaryOrStandaloneAddress)
      url += "/api/v1/address/" + editingId;
    else if (isEditing && isAlternateAddress)
      url += "/api/v1/address/alternative/" + editingId;

    const result = (await fetchSingleton(url, {
      method: (isCreating && "POST") || (isEditing && "PUT") || "GET",
      body: JSON.stringify({
        ...data,
        is_direct: false,
        ...(isEditing ? { is_approved: true } : {}),
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    })) as ApiResponseDto<IFormInput>;

    const addressSaveCanBeForcedWithErrorCodes = new Set([
      "ADDRESS_VALIDATION_AND_PRIMARY_UPDATE_WARNING",
      "ADDRESS_PRIMARY_ALTERNATIVE_UPDATE_WARNING",
      "ADDRESS_VALIDATION_FAILED",
      "ADDRESS_PRIMARY_WITH_ALTERNATES_WARNING",
    ]);

    if (
      result.status === "ERROR" &&
      result.errors.some(it =>
        addressSaveCanBeForcedWithErrorCodes.has((it as ErrorT).machineCode),
      ) &&
      result.meta?.addressSaveCanBeForced
    ) {
      openModalQuestion({
        title: "Question",
        message:
          (
            (result.errors.find(it =>
              addressSaveCanBeForcedWithErrorCodes.has((it as ErrorT).machineCode),
            ) || result.errors[0]) as ErrorT | undefined
          )?.message || "Unknown error occurred.",
        variants: [
          {
            slug: "reject",
            title: "No",
            action: async () => finishInteraction(),
          },
          {
            slug: "accept",
            title: "Yes",
            variant: "submit",
            action: async () => {
              try {
                const result = (await fetchSingleton(withQuery(url, { force: true }), {
                  method: (isCreating && "POST") || (isEditing && "PUT") || "GET",
                  body: JSON.stringify({
                    ...data,
                    is_direct: false,
                    ...(isEditing ? { is_approved: true } : {}),
                  }),
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${idToken}`,
                  },
                })) as ApiResponseDto<IFormInput>;

                handleApiResult(result);

                const msg = (() => {
                  if (isCreating) return "Address added successfully";
                  if (isEditing) return "Address updated successfully";
                })();

                toast.success(msg, { autoClose: 5000 });

                finishInteraction();
              } catch (e) {
                if (INTERRUPTED) return; // Do nothing
                console.error(e);
                toast.error(humanizeError(e));
              }
            },
          },
        ],
      });
      return;
    }

    if (
      result.status === "ERROR" &&
      result.errors.some(
        it => (it as ErrorT).machineCode === "ADDRESS_ALTERNATE_WITH_PRIMARY_WARNING",
      ) &&
      result.meta?.addressFillIn
    ) {
      const addressFillIn = (values: Partial<Address>) =>
        ({
          ..._.mapValues(values, () => ""),
          curbside_households: 1,
          zone_id: zones?.length === 1 ? zones[0].id : values.zone_id,
          program_id: selectedProgramIdInUi ? +selectedProgramIdInUi : undefined,
          ...(result.meta?.addressFillIn as Partial<Address>),
        }) as Address;
      async function onAccept() {
        reset(addressFillIn);
        await trigger();
      }
      openModalQuestion({
        title: "Question",
        message:
          (
            (result.errors.find(
              it =>
                (it as ErrorT).machineCode === "ADDRESS_ALTERNATE_WITH_PRIMARY_WARNING",
            ) || result.errors[0]) as ErrorT | undefined
          )?.message || "Unknown error occurred.",
        variants: [
          {
            slug: "reject",
            title: "Cancel",
            action: async () => finishInteraction(),
          },
          {
            slug: "accept",
            title: "Okay",
            variant: "submit",
            action: onAccept,
          },
        ],
      });
      return;
    }

    handleApiResult(result);

    const msg = (() => {
      if (isCreating) return "Address added successfully";
      if (isEditing) return "Address updated successfully";
    })();

    toast.success(msg, { autoClose: 5000 });

    finishInteraction();
  };

  React.useEffect(() => {
    if (setConfirmClose) setConfirmClose(isDirty);
  }, [isDirty]);

  return (
    <FormProvider {...formMethods}>
      <PartLoader block active={loaders.some(Boolean)}>
        <form
          className={formStyles.form}
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          {isEditing && isAlternateAddress && (
            <div className={formStyles.alternateAddressWarn}>
              <div className={formStyles.alternateAddressWarnIcon}>
                <InfoIcon />
              </div>
              <div className={formStyles.alternateAddressWarnText}>
                You`re viewing an alternate address with limited editable fields. To make
                major updates, please edit the primary address{" "}
                <button
                  onClick={() =>
                    maybeParentOfExistingItem?.id !== undefined &&
                    startEdit(maybeParentOfExistingItem?.id)
                  }
                  onKeyDown={() =>
                    maybeParentOfExistingItem?.id !== undefined &&
                    startEdit(maybeParentOfExistingItem?.id)
                  }
                  data-tooltip-id="start-edit-primary"
                  type="button"
                >
                  <u>
                    {maybeParentOfExistingItem?.street_number}{" "}
                    {maybeParentOfExistingItem?.street_full_name}
                  </u>
                </button>{" "}
                directly (any changes made there could impact all related alternatives)
              </div>
              <Tooltip id="start-edit-primary" content="Click to edit primary address" />
            </div>
          )}
          {(isCreating ||
            (isEditing && loaders.some(Boolean)) ||
            (isEditing && isPrimaryOrStandaloneAddress)) && (
            <>
              <label className={formStyles.formLabel} htmlFor="autocomplete">
                Address Autocomplete
              </label>
              <PartAddressSuggestions
                id="autocomplete"
                onChange={async addressSuggestion => {
                  reset(
                    (values: Partial<Address>) =>
                      ({
                        ..._.mapValues(values, () => ""),
                        curbside_households: 1,
                        zone_id: zones?.length === 1 ? zones[0].id : values.zone_id,
                        program_id: selectedProgramIdInUi
                          ? +selectedProgramIdInUi
                          : undefined,
                        ...(addressSuggestion as Partial<Address>),
                      }) as Address,
                  );
                  await trigger();
                }}
              />
              <hr className={formStyles.formHLine} />
            </>
          )}
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="unit">
                Unit
              </label>
              <input
                id="unit"
                className={formStyles.formInput}
                {...register("unit")}
                aria-invalid={!!errors.unit}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="unit" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="street_number">
                Street Number <sup>*</sup>
              </label>
              <input
                id="street_number"
                className={formStyles.formInput}
                {...register("street_number")}
                aria-invalid={!!errors.street_number}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="street_number" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="street_full_name">
                Street Name <sup>*</sup>
              </label>
              <input
                id="street_full_name"
                className={formStyles.formInput}
                {...register("street_full_name")}
                aria-invalid={!!errors.street_full_name}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="street_full_name" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="city">
                City <sup>*</sup>
              </label>
              <input
                id="city"
                className={formStyles.formInput}
                {...register("city")}
                aria-invalid={!!errors.city}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="city" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="program_id">
                Province <sup>*</sup>
              </label>
              <div className={formStyles.formSelect}>
                <select
                  id="program_id"
                  data-placeholder={!watch("program_id") ? "" : undefined}
                  {...register("program_id")}
                  aria-invalid={!!errors.program_id}
                  disabled={isAlternateAddress}
                >
                  <option hidden value="">
                    Please Choose...
                  </option>
                  {programsItems?.map(program => (
                    <option value={String(program?.id)} key={program?.id}>
                      {program?.name}
                    </option>
                  ))}
                </select>
              </div>
              <PartFormError name="program_id" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="postal_code">
                Postal Code
              </label>
              <input
                id="postal_code"
                className={formStyles.formInput}
                {...register("postal_code")}
                aria-invalid={!!errors.postal_code}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="postal_code" />
            </div>
          </div>
          <hr className={formStyles.formHLine} />
          {!isAlternateAddress && (
            <div className={formStyles.formMultipleColumns}>
              <div>
                <label className={formStyles.formLabel} htmlFor="curbside_households">
                  Curbside Households <sup>*</sup>
                </label>
                <input
                  id="curbside_households"
                  className={formStyles.formInput}
                  {...register("curbside_households")}
                  aria-invalid={!!errors.curbside_households}
                  readOnly={isAlternateAddress}
                />
                <PartFormError name="curbside_households" />
              </div>
              <div>
                <label className={formStyles.formLabel} htmlFor="collection_route">
                  Collection Route
                </label>
                <input
                  id="collection_route"
                  className={formStyles.formInput}
                  {...register("collection_route")}
                  aria-invalid={!!errors.collection_route}
                  readOnly={isAlternateAddress}
                />
                <PartFormError name="collection_route" />
              </div>
            </div>
          )}
          {!isAlternateAddress && (
            <div className={formStyles.formMultipleColumns}>
              <div>
                <label
                  className={formStyles.formLabel}
                  htmlFor="collection_household_start_date"
                >
                  Collection Household Start Date
                </label>
                <PartFormDatePicker
                  id="collection_household_start_date"
                  control={control}
                  name={"collection_household_start_date"}
                  placeholder="Select Date"
                  // Dec 2, 2024 (Tuesday)
                  dateFormat="MMM d, yyyy (EEE)"
                  disabled={isAlternateAddress}
                />
                <PartFormError name="collection_household_start_date" />
              </div>
            </div>
          )}
          {!isAlternateAddress && <hr className={formStyles.formHLine} />}
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="lat">
                Latitude {/* <sup>*</sup> */}
              </label>
              <input
                id="lat"
                className={formStyles.formInput}
                {...register("lat")}
                aria-invalid={!!errors.lat}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="lat" />
            </div>
            <div>
              <label className={formStyles.formLabel} htmlFor="lon">
                Longitude {/* <sup>*</sup> */}
              </label>
              <input
                id="lon"
                className={formStyles.formInput}
                {...register("lon")}
                aria-invalid={!!errors.lon}
                readOnly={isAlternateAddress}
              />
              <PartFormError name="lon" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="zone_id">
                Zone <sup>*</sup>
              </label>
              <div className={formStyles.formSelect}>
                <select
                  id="zone_id"
                  data-placeholder={!watch("zone_id") ? "" : undefined}
                  {...register("zone_id")}
                  aria-invalid={!!errors.zone_id}
                  disabled={isAlternateAddress}
                >
                  <option hidden value="">
                    Please Choose...
                  </option>
                  {zones
                    ?.sort((a, b) => a.code - b.code)
                    .map(zone => (
                      <option value={zone?.id} key={zone?.id}>
                        {(() => {
                          let text = getZoneName(zone);
                          if (text.length >= 65) text = text.slice(0, 65) + "...";
                          return text;
                        })()}
                      </option>
                    ))}
                </select>
              </div>
              <PartFormError name="zone_id" />
            </div>
          </div>
          <div className={formStyles.formMultipleColumns}>
            <div>
              <label className={formStyles.formLabel} htmlFor="notes">
                Notes
              </label>
              <textarea
                id="notes"
                className={formStyles.formTextarea}
                placeholder="e.g. Address may need to be validated manually."
                {...register("notes")}
                aria-invalid={!!errors.notes}
              />
              <PartFormError name="notes" />
            </div>
          </div>
          <div className={formStyles.formActionButtonsContainer}>
            <button
              type="button"
              className={formStyles.buttonReset}
              data-test-element-type="button"
              data-test-action-type="reset"
              onClick={() => {
                reset();
                finishInteraction();
              }}
            >
              Discard
            </button>
            {(isCreating || (isEditing && isPrimaryOrStandaloneAddress)) && (
              <button
                type="button"
                className={formStyles.buttonReset}
                data-test-element-type="button"
                data-test-action-type="validate"
                onClick={startValidate}
              >
                <PartLoader active={isValidateRunning}>Check</PartLoader>
              </button>
            )}
            {(isCreating || (isEditing && isPrimaryOrStandaloneAddress)) && (
              <PartFormSaveButton isSubmitting={isSubmitting}>
                Save as Verified
              </PartFormSaveButton>
            )}
            {isEditing && isAlternateAddress && (
              <PartFormSaveButton isSubmitting={isSubmitting}>Save</PartFormSaveButton>
            )}
          </div>
        </form>
      </PartLoader>
    </FormProvider>
  );
});

AddressEditor.displayName = "AddressEditor";

const ModalAddress = React.forwardRef<PartModalRef>((props, ref) => {
  const { isCreating, isEditing, finishInteraction } = useModalAddressEditor();

  const title =
    (isCreating && "Add New Address") ||
    (isEditing && "Edit Address") ||
    "Unknown action";

  const [shouldConfirmClose, setShouldConfirmClose] = React.useState<boolean>(false);

  const formRef = React.useRef<IFormInputRef>(null);

  return (
    <PartModal
      ref={ref}
      title={title}
      showed={isCreating || isEditing}
      onClose={() => finishInteraction()}
      afterClose={() => formRef.current?.reset()}
      confirmCloseQuestion={shouldConfirmClose}
    >
      <AddressEditor setConfirmClose={setShouldConfirmClose} ref={formRef} />
    </PartModal>
  );
});

ModalAddress.displayName = "ModalAddress";

export default ModalAddress;
